import { LATE_RECORDING_CODES, OPEN_ANSWER_REJECT_CODES, RECORD_FILE_LOST } from './apiErrorCode';
import { LESSON_AUDIO_KIND, type LessonAudioPayload } from './lessonAudio';
import { isOutboxItemStale, type OutboxItem } from './outbox';

/**
 * Неотправленные записи голоса — что показать ученику в профиле и в «Домашках» (промпт 22, 18.10.2026).
 *
 * ⚠️ МЕТОК У СТРОК НЕТ (решение владельца 18.10, макеты v2). В списке «Неотправленные записи» всё и так не
 * отправлено: строка — урок, задание, длительность. Во время отправки — полоска под строкой. Красной строкой —
 * только проблема: запись лежит дольше недели, запись не удалось отправить, сервер её не принял.
 *
 * Состояние выводится из записи очереди одной функцией, а не хранится полем: у записей, лежащих у учеников с
 * прежней версии приложения, никаких новых полей нет, а показать их нужно так же.
 */
export type RecordQueueProblem =
  /** лежит дольше недели (`isOutboxItemStale`) — можно отправить или удалить */
  | 'stale'
  /** последняя попытка не удалась по временной причине (сеть, 5xx, 408, 429) — «Отправить» и «Удалить» */
  | 'retry'
  /** сервер ответил отказом «навсегда», но запись не снята (`permanentFail`) — можно только удалить */
  | 'failed';

/**
 * Почему не ушла — ключ текста (решение владельца 18.10: «временная причина — „Отправить“; насовсем — только
 * „Удалить“, причина человеческим текстом»). Временные: `network` (запрос не дошёл), `server` (5xx, 408),
 * `busy` (429), `session` (401 при живом сеансе). Насовсем: `tooLarge` (413), `forbidden` (403), `gone` (404/410),
 * `unknown` (прочие 4xx и записи прежних версий без причины).
 */
export type FailReason = 'network' | 'server' | 'busy' | 'session' | 'tooLarge' | 'forbidden' | 'gone' | 'unknown';

export function failReasonOf(status: number | undefined, permanent: boolean): FailReason {
  if (permanent) {
    if (status === 413) return 'tooLarge';
    if (status === 403) return 'forbidden';
    if (status === 404 || status === 410) return 'gone';
    return 'unknown';
  }
  if (!status) return 'network';
  if (status === 429) return 'busy';
  if (status === 401) return 'session';
  return 'server';
}

export type RecordQueueRow = {
  /** id записи очереди: `lesson-audio:<blockId>` */
  id: string;
  blockId: string;
  lessonId?: string;
  lessonTitle?: string;
  sectionId?: string;
  sectionTitle?: string;
  /** длительность по часам страницы, секунды */
  seconds?: number;
  /** когда запись легла в очередь, мс */
  at: number;
  sending: boolean;
  problem?: RecordQueueProblem;
  /** у `retry` и `failed` — почему; у `stale` — тоже, если попытки были */
  reason?: FailReason;
};

/**
 * Строки списка из очереди. Только записи голоса, только свои (чужие очередь отдаёт и так — `queuedOfKind`
 * отсекает по владельцу). Порядок — как в очереди: от старой к новой, в том же порядке они и уйдут.
 */
export function recordQueueRows(
  items: readonly OutboxItem[],
  opts: { sendingId?: string | null; now?: number } = {},
): RecordQueueRow[] {
  const now = opts.now ?? Date.now();
  return items
    .filter((i) => i.kind === LESSON_AUDIO_KIND)
    .map((i) => {
      const p = (i.payload ?? {}) as Partial<LessonAudioPayload>;
      // Отказ навсегда важнее срока: отправить всё равно нельзя. Срок важнее временной неудачи: обе — «Отправить»,
      // но «давно не отправлена» ученику понятнее, чем «сервер не ответил» неделю назад.
      const tried = i.attempts > 0 && typeof i.lastStatus === 'number';
      const problem: RecordQueueProblem | undefined = i.permanentFail
        ? 'failed'
        : isOutboxItemStale(i, now)
          ? 'stale'
          : tried
            ? 'retry'
            : undefined;
      const reason = i.permanentFail ? failReasonOf(i.lastStatus, true) : tried ? failReasonOf(i.lastStatus, false) : undefined;
      return {
        id: i.id,
        blockId: p.blockId ?? i.id,
        lessonId: p.lessonId,
        lessonTitle: p.lessonTitle,
        sectionId: p.sectionId,
        sectionTitle: p.sectionTitle,
        seconds: typeof p.clientSeconds === 'number' && Number.isFinite(p.clientSeconds) ? p.clientSeconds : undefined,
        at: i.createdAt,
        sending: !!opts.sendingId && opts.sendingId === i.id,
        problem,
        reason,
      };
    });
}

/** 102 → «1:42». Нет длительности — `null`, строка покажется без неё. */
export function formatRecordDuration(seconds: number | undefined): string | null {
  if (typeof seconds !== 'number' || !Number.isFinite(seconds) || seconds <= 0) return null;
  const s = Math.round(seconds);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

/** 16.10, 21:05 — по часам устройства. */
export function formatRecordedAt(ms: number): string {
  const d = new Date(ms);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${p(d.getDate())}.${p(d.getMonth() + 1)}, ${p(d.getHours())}:${p(d.getMinutes())}`;
}

/**
 * Можно ли предложить «Отправить» (решение владельца 18.10.2026). Удаление — единственное необратимое действие, к
 * нему подталкиваем только при однозначном ответе: 413 (такую не примут никогда — «Прослушать» в листе есть) и
 * 404/410 (задания нет). 403 бывает временным (тумблеры навыков, доступ ученика), прочие 4xx — неизвестно: у них
 * «Отправить» есть. 409 сюда не доходит — такую запись очередь снимает сама («Не приняты»).
 */
export function canResend(row: Pick<RecordQueueRow, 'problem' | 'reason'>): boolean {
  return !(row.problem === 'failed' && (row.reason === 'tooLarge' || row.reason === 'gone'));
}

/** Запись к разделу домашки: «Сдать» в «Домашках» есть только у раздела, к которому в очереди лежит запись. */
export function recordForSection(rows: readonly RecordQueueRow[], sectionId: string): RecordQueueRow | undefined {
  if (!sectionId) return undefined;
  return rows.find((r) => r.sectionId === sectionId && canResend(r));
}

// ─── «Не приняты»: отказы сервера, которые очередь уже сняла ───────────────────────────────────────────────

/**
 * Почему запись не принята — ключ текста, а не код сервера: тексты — в `recordQueue.*` трёх локалей.
 *
 * ⚠️ ЗАЧЕМ СВОЙ СПИСОК, А НЕ ЗАМЕТКА ЗАДАНИЯ. `addRecordNotice` хранит только задание и код и показывается у
 * задания один раз — `takeRecordNotice` её съедает. В профиле отказ должен висеть, пока ученик не нажмёт
 * «Понятно», и знать урок: запись к этому моменту из очереди уже снята, спросить больше не у кого.
 */
export type RejectReason = 'already_reviewed' | 'superseded' | 'file_lost' | 'bad_recording';

/**
 * ⚠️ «ПУСТАЯ ИЛИ БИТАЯ ЗАПИСЬ» — ТОЖЕ СЮДА (найдено 18.10.2026). Эти отказы (`OPEN_ANSWER_REJECT_CODES`) очередь
 * снимает сразу — повтор получит тот же ответ. Прежде в профиле от такой записи не оставалось ничего: строка просто
 * исчезала. Теперь — «Запись не получилась — запишите ответ заново» до «Понятно».
 */
export function rejectReasonOf(code: string | undefined): RejectReason | undefined {
  if (code === 'already_reviewed') return 'already_reviewed';
  if (code === 'answer_superseded') return 'superseded';
  if (code === RECORD_FILE_LOST) return 'file_lost';
  if (code && OPEN_ANSWER_REJECT_CODES.has(code) && code !== 'essay_empty') return 'bad_recording';
  // Новый «опоздавший» код сервера — тоже отказ, текст как у принятой работы.
  if (code && LATE_RECORDING_CODES.has(code)) return 'already_reviewed';
  return undefined;
}

export type RejectedRecord = {
  blockId: string;
  reason: RejectReason;
  /** когда пришёл отказ, мс */
  at: number;
  lessonTitle?: string;
  sectionTitle?: string;
  seconds?: number;
};

const REJECTED_KEY = 'onebilim:recordRejected';
/** Сколько помним: отказ — редкость; больше десятка значит, что ученик не открывал профиль неделями. */
const REJECTED_KEEP = 20;

function readRejected(): RejectedRecord[] {
  try {
    const raw = globalThis.localStorage?.getItem(REJECTED_KEY);
    const list = raw ? (JSON.parse(raw) as unknown) : [];
    return Array.isArray(list) ? (list.filter((r) => r && typeof r.blockId === 'string') as RejectedRecord[]) : [];
  } catch {
    return [];
  }
}

function writeRejected(list: readonly RejectedRecord[]): void {
  try {
    globalThis.localStorage?.setItem(REJECTED_KEY, JSON.stringify(list.slice(-REJECTED_KEEP)));
  } catch {
    // Хранилище недоступно (частный режим, переполнено) — отказ останется заметкой у задания, этого хватит.
  }
}

const rejectedListeners = new Set<() => void>();

/** Подписка на изменения списка — профиль перерисовывается без перезагрузки. */
export function onRejectedRecordsChange(l: () => void): () => void {
  rejectedListeners.add(l);
  return () => rejectedListeners.delete(l);
}

/** Запомнить отказ. Повтор по тому же заданию заменяет прежний: «Понятно» нужен один на задание. */
export function addRejectedRecord(payload: Partial<LessonAudioPayload>, code: string | undefined, now = Date.now()): void {
  const reason = rejectReasonOf(code);
  if (!reason || !payload.blockId) return;
  const list = readRejected().filter((r) => r.blockId !== payload.blockId);
  list.push({
    blockId: payload.blockId,
    reason,
    at: now,
    lessonTitle: payload.lessonTitle,
    sectionTitle: payload.sectionTitle,
    seconds: typeof payload.clientSeconds === 'number' ? payload.clientSeconds : undefined,
  });
  writeRejected(list);
  rejectedListeners.forEach((l) => l());
}

export function listRejectedRecords(): RejectedRecord[] {
  return readRejected();
}

/** «Понятно»: строка уходит из списка. */
export function dismissRejectedRecord(blockId: string): void {
  writeRejected(readRejected().filter((r) => r.blockId !== blockId));
  rejectedListeners.forEach((l) => l());
}
